import { Component, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';

import { CustomEventService } from '@shared/services/custom-event.service';
import { WidgetService } from '@shared/services/widget.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { WidgetStatePlaceholderEnum } from '@shared/enums/widget-state-placeholder.enum';

import AxisLineUtil from '@shared/utils/axis-line.util';
import { commonLineChartUtil } from '@shared/utils/common-line-chart.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EChartOption, ECharts } from 'echarts';

@UntilDestroy()
@Component({
  selector: 'app-performance-metrics-widget',
  templateUrl: './performance-metrics-widget.component.html'
})
export class PerformanceMetricsWidgetComponent implements OnInit {

  @Input() dateFilterable: boolean;
  widgetId: string;
  widgetName: string;
  widgetType: string;
  widgetFooter: Widget.FooterModel;
  headerAddon: string;
  headerComponents: [] = [];
  selectedDate: string;
  note: string;
  url: string;
  from: number;
  showDownloadIcon: boolean;

  @Input()
  set period(period: string) {
    if (period === 'FROM') {
      this.selectedDate = this.from.toString(10);
    } else if (period === 'TODAY') {
      this.selectedDate = `${period}`;
    } else if (period) {
      this.selectedDate = `FROM_LAST_${period}`;
    }
  };

  loading: boolean;
  initialLoading: boolean;
  widgetStatePlaceholderEnum = WidgetStatePlaceholderEnum;
  showWidgetStatePlaceholder: WidgetStatePlaceholderEnum;

  countData: any[];
  chartOptionMap: Map<string, any> = new Map();
  chartInstanceMap: Map<string, ECharts> = new Map();

  toolTipHTML: string;

  constructor(
    private customEventService: CustomEventService,
    private widgetService: WidgetService
  ) { }

  ngOnInit(): void {
    this.toolTipHTML = this.headerComponents.join('<br>');

    this.loadWidgetData(true);

    // Subscribe to the dashboard announcements to update
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.UPDATE_WIDGET_DATA) {
        this.loadWidgetData();
      }

      if (action === ActionTypesEnum.UPDATE_WIDGET_DATE) {
        const { id, date } = data;

        if (this.widgetId === id ) {
          this.selectedDate = date;
          this.loadWidgetData();
        }
      }
    });
  }

  loadWidgetData(initial?: boolean): void {
    this.loading = true;

    if (initial) {
      this.initialLoading = true;
    }

    this.widgetService.get(this.widgetId, this.selectedDate)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        this.loading = false;
        this.initialLoading = false;

        if (!response.data) {
          this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
          return;
        }
        this.showWidgetStatePlaceholder = undefined;

        if (response.footer) {
          this.widgetFooter = response.footer;
        }

        this.countData = response.data.map((count, i, arr) => {
          count.calculatedClass = `count--1-of-${arr.length}`;

          return count;
        });

        for (const data of this.countData) {
          if (data.chartInfo && data.chartInfo.type === 'gauge') {
            if (initial) {
              this.chartOptionMap.set('gauge', {
                series: [{
                  type: 'gauge',
                  startAngle: 180,
                  endAngle: 0,
                  center: ['50%', '60%'],
                  radius: '80%',
                  min: 0,
                  max: 1,
                  splitNumber: 0,
                  axisLine: AxisLineUtil.generateAxisLine(data.chartInfo.data),
                  axisTick: {
                    show: false
                  },
                  axisLabel: {
                    show: false
                  },
                  splitLine: {
                    show: false
                  },
                  pointer: {
                    show: false
                  },
                  title: {
                    show: true,
                    offsetCenter: ['0%', '10%'],
                    textStyle: {
                      fontSize: 14,
                      color: '#666665',
                      fontFamily: 'Arial',
                      fontWeight: 400
                    },
                  },
                  detail: {
                    formatter: '{value}',
                    offsetCenter: [0, -20],
                    textStyle: {
                      fontSize: 22,
                      color: '#666666',
                      fontFamily: 'Arial'
                    }
                  },
                  data: [
                    {
                      value: data.chartInfo.data,
                      name: 'Apdex Score'
                    }
                  ]
                }]
              });
            } else {
              this.chartInstanceMap.get('gauge').setOption({
                series: [{
                  data: [{
                    value: data.chartInfo.data,
                    name: 'Apdex Score',
                  }],
                  axisLine: AxisLineUtil.generateAxisLine(data.chartInfo.data)
                }]
              });
            }
          }

          if (data.chartInfo && data.chartInfo.type === 'line') {
            const chartCreator = (type: 'success' | 'fail') => {
              return commonLineChartUtil({
                grid: {
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0
                },
                yAxisData: data.chartInfo.data,
                xAxisData: data.chartInfo.date,
                tooltipFormatter: `{c} ${data.title.quantity ? data.title.quantity : ''}<br>{b}`,
                type,
                opacity: 0.3
              });
            };

            const updateData = (instance: any) => {
              instance.setOption({
                series: [{
                  data: data.chartInfo.data
                }],
                xAxis: {
                  data: data.chartInfo.date
                },
                yAxis: {
                  data: data.chartInfo.data
                }
              });
            };

            if (initial) {
              this.chartOptionMap.set(data.chartInfo.status, chartCreator(data.chartInfo.status));
            } else {
              updateData(this.chartInstanceMap.get(data.chartInfo.status));
            }
          }
        }
      }, error => {
        console.log('error while fetching widget data', error);
        this.loading = false;
        this.initialLoading = false;
        this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
      });
  }

  onChartInit($event: any, status: string): void {
    this.chartInstanceMap.set(status, $event);
  }

  trackByFn(index): void {
    return index;
  }
}
